<template>
  <div id="app">
    <nav class="navbar" :class="{ scrolled: isScrolled }" v-if="!isAuthPage">
      <div class="nav-container">
        <div class="logo-box">
          <router-link to="/">
            <img src="./assets/logo_big.png" alt="AutoSquare Logo" class="logo" />
          </router-link>
        </div>
        <button
          class="menu-toggle"
          :class="{ 'is-active': isMenuOpen }"
          @click="toggleMenu"
          aria-label="切换导航菜单"
        >
          <span class="hamburger-box">
            <span class="hamburger-inner"></span>
          </span>
        </button>
        <ul class="nav-links" :class="{ 'nav-active': isMenuOpen }">
          <li v-if="$route.path === '/'">
            <a
              class="primary-nav-link"
              href="#home"
              @click.prevent="scrollTo('home')"
              :class="{ active: currentSection === 'home' }"
            >首页</a>
          </li>
          <li v-else>
            <router-link class="primary-nav-link" to="/">首页</router-link>
          </li>
          <li>
            <a
              v-if="$route.path === '/'"
              class="primary-nav-link"
              href="#products"
              @click.prevent="scrollTo('products')"
              :class="{ active: currentSection === 'products' }"
            >产品</a>
            <router-link v-else class="primary-nav-link" :to="{ path: '/', hash: '#products' }">产品</router-link>
          </li>
          <li>
            <a
              v-if="$route.path === '/'"
              class="primary-nav-link"
              href="#team"
              @click.prevent="scrollTo('team')"
              :class="{ active: currentSection === 'team' }"
            >团队成员</a>
            <router-link v-else class="primary-nav-link" :to="{ path: '/', hash: '#team' }">团队成员</router-link>
          </li>
          <li>
            <a
              v-if="$route.path === '/'"
              class="primary-nav-link"
              href="#about"
              @click.prevent="scrollTo('about')"
              :class="{ active: currentSection === 'about' }"
            >关于</a>
            <router-link v-else class="primary-nav-link" :to="{ path: '/', hash: '#about' }">关于</router-link>
          </li>
          <li class="auth-menu">
            <AccountMenu
              :user="authStore.user"
              :account-portal-url="authStore.accountPortalUrl"
              :login-url="loginUrl"
              :logout-action="handleLogout"
            />
          </li>
        </ul>
      </div>
    </nav>

    <main :class="{ 'page-content': $route.path !== '/' && !isAuthPage }">
      <router-view />
    </main>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import { useAuthStore } from './store/auth'
import { useRouter } from 'vue-router'
import AccountMenu from './components/AccountMenu.vue'
import { buildSsoLoginUrl } from './utils/sso'

export default {
  name: 'App',
  components: {
    AccountMenu
  },
  data() {
    return {
      isScrolled: false,
      currentSection: 'home',
      isMenuOpen: false
    }
  },
  computed: {
    ...mapStores(useAuthStore),
    loginUrl() {
      return buildSsoLoginUrl()
    },
    isAuthPage() {
      return Boolean(this.$route.meta?.isAuthBridge)
        || this.$route.name === 'login'
        || this.$route.name === 'login-complete'
        || this.$route.name === 'register'
    }
  },
  setup() {
    const router = useRouter()
    return { router }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll)
    this.setupObserver()
  },
  unmounted() {
    window.removeEventListener('scroll', this.handleScroll)
    if (this.observer) this.observer.disconnect()
  },
  watch: {
    $route() {
      this.isMenuOpen = false
      this.currentSection = ''
    }
  },
  methods: {
    async handleLogout() {
      await this.authStore.logout()
    },
    handleScroll() {
      if (this.$route.path !== '/') {
        this.isScrolled = true
        return
      }
      this.isScrolled = window.scrollY > 50
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen
    },
    scrollTo(sectionId) {
      this.isMenuOpen = false
      if (this.$route.path !== '/') {
        this.router.push({ path: '/', hash: `#${sectionId}` }).then(() => {
          this.doScroll(sectionId)
        })
      } else {
        this.doScroll(sectionId)
      }
    },
    doScroll(sectionId) {
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          window.scrollTo({
            top: element.offsetTop - 70,
            behavior: 'smooth'
          })
        }
      }, 100)
    },
    setupObserver() {
      const options = {
        root: null,
        rootMargin: '-50% 0px -50% 0px',
        threshold: 0
      }
      this.observer = new IntersectionObserver((entries) => {
        if (this.$route.path !== '/') return
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.currentSection = entry.target.id
          }
        })
      }, options)
      this.observeSections()
    },
    observeSections() {
      const sections = document.querySelectorAll('main > section')
      sections.forEach((section) => {
        this.observer.observe(section)
      })
    }
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

:root {
  --bg-color-primary: #ffffff;
  --bg-color-secondary: #f8fafc;
  --text-primary: #0f172a;
  --text-secondary: #64748b;
  --accent-color: #2563eb;
  --accent-hover: #1d4ed8;
  --nav-height: 80px;
}

* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html,
body,
#app {
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
  background-color: var(--bg-color-primary);
  color: var(--text-primary);
  scroll-behavior: smooth;
  line-height: 1.6;
  overflow-x: hidden;
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

main {
  flex: 1;
}

.navbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: var(--nav-height);
  background-color: transparent;
  transition: all 0.3s ease;
  z-index: 1000;
  padding: 0 5%;
}

.navbar.scrolled {
  background-color: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  height: 70px;
}

.nav-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
}

.logo-box {
  display: flex;
  align-items: center;
}

.logo {
  height: 48px;
  width: auto;
  object-fit: contain;
  transition: height 0.3s ease;
}

.navbar.scrolled .logo {
  height: 40px;
}

.nav-links {
  display: flex;
  list-style: none;
  gap: 2rem;
  align-items: center;
}

.primary-nav-link {
  text-decoration: none;
  color: var(--text-primary);
  font-weight: 500;
  font-size: 1rem;
  transition: color 0.2s ease;
  position: relative;
}

.primary-nav-link::after {
  content: '';
  position: absolute;
  width: 0%;
  height: 2px;
  bottom: -4px;
  left: 0;
  background-color: var(--accent-color);
  transition: width 0.3s ease;
}

.primary-nav-link:hover,
.primary-nav-link.active,
.nav-links .router-link-exact-active {
  color: var(--accent-color);
}

.primary-nav-link:hover::after,
.primary-nav-link.active::after,
.nav-links .router-link-exact-active::after {
  width: 100%;
}

.auth-menu {
  display: flex;
  align-items: center;
}

.menu-toggle {
  display: none;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  z-index: 1001;
}

.hamburger-box {
  width: 24px;
  height: 24px;
  display: inline-block;
  position: relative;
}

.hamburger-inner {
  display: block;
  top: 50%;
  margin-top: -2px;
}

.hamburger-inner,
.hamburger-inner::before,
.hamburger-inner::after {
  width: 24px;
  height: 2px;
  background-color: var(--text-primary);
  border-radius: 4px;
  position: absolute;
  transition: transform 0.15s ease;
}

.hamburger-inner::before,
.hamburger-inner::after {
  content: "";
  display: block;
}

.hamburger-inner::before {
  top: -8px;
}

.hamburger-inner::after {
  bottom: -8px;
}

.menu-toggle.is-active .hamburger-inner {
  transform: rotate(45deg);
}

.menu-toggle.is-active .hamburger-inner::before {
  top: 0;
  opacity: 0;
}

.menu-toggle.is-active .hamburger-inner::after {
  bottom: 0;
  transform: rotate(-90deg);
}

@media (max-width: 768px) {
  .menu-toggle {
    display: block;
  }

  .nav-links {
    position: fixed;
    top: 0;
    right: -100%;
    width: 250px;
    height: 100vh;
    background-color: var(--bg-color-primary);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3rem;
    transition: right 0.3s ease;
    box-shadow: -5px 0 15px rgba(0, 0, 0, 0.05);
    z-index: 1000;
  }

  .nav-links.nav-active {
    right: 0;
  }

  .navbar {
    padding: 0 1rem;
  }
}

.page-content {
  padding-top: calc(var(--nav-height) + 2rem);
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: var(--bg-color-secondary);
}
</style>
